import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import api from 'api';
import DocumentsIcon from 'icons/Documents.icon';
import { DataRoomNode } from '../../models/DataRoomNode';
import DocumentStatusBadge from 'components/DocumentStatusBadge';
import { DataRoomMember } from '../../models/DataRoomMember';
import { FileAccessSelector } from 'components/FileAccessSelector';
import { Checkbox } from 'ncoded-component-library';
import { getIsNodeFile } from 'utils';

import './MemberFileAccessSelector.styles.scss';

export const PARENT_CLASS = 'member-file-access-selector';

export type MemberFileAccessSelectorProps = {
  className?: string;
  dataRoomId: string;
  dataRoomMemberId?: string;
  refetchFlag?: boolean;
  isDisabled?: boolean;
  defaultOpen?: boolean;
  showSelectAllCheckbox?: boolean;
  isLoadingHasFullAccess?: boolean;
  hasFullAccess?: boolean; // if true, sellect all is checked
  onHasFullAccessChange?: (hasFullAccess: boolean) => void;
  updateMember?: (member: DataRoomMember) => void;
};

const MemberFileAccessSelector: React.FC<MemberFileAccessSelectorProps> = (
  props,
) => {
  const {
    className,
    dataRoomId,
    dataRoomMemberId,
    refetchFlag,
    isDisabled,
    defaultOpen = false,
    hasFullAccess = false,
    showSelectAllCheckbox = false,
    isLoadingHasFullAccess = false,
    onHasFullAccessChange,
    updateMember,
  } = props;
  const [checkedFilesIds, setCheckedFilesIds] = useState<string[]>([]);

  const classes = classNames(PARENT_CLASS, 'white-panel', className);

  const { t } = useTranslation();

  const getChildrenForNode = useCallback(
    async (id?: string) => {
      const {
        data: { dataRoomNodes, dataRoomMember },
      } = await api.dataRoom.nodes.getChildrenForMember(
        dataRoomId,
        id || null,
        dataRoomMemberId,
      );

      setCheckedFilesIds([
        ...dataRoomMember.uploadedFileIds,
        ...dataRoomMember.accessableDataRoomNodesView,
      ]);

      return dataRoomNodes;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataRoomId, dataRoomMemberId, refetchFlag],
  );

  const updateFileAccess = useCallback(
    async (newNode: { dataRoomNodeId: string; enable: boolean }) => {
      const {
        data: { dataRoomMember },
      } = await api.dataRoom.members.updateMemberDocuments(dataRoomMemberId, {
        changedPermissions: [newNode],
      });

      setCheckedFilesIds([
        ...dataRoomMember.uploadedFileIds,
        ...dataRoomMember.accessableDataRoomNodesView,
      ]);
      updateMember?.(dataRoomMember);
    },
    [dataRoomMemberId, updateMember],
  );

  const checkIfNodeIsDisabled = useCallback(
    (node: DataRoomNode) =>
      isDisabled ||
      (getIsNodeFile(node) && node.dataRoomOwner?._id === dataRoomMemberId),
    [isDisabled, dataRoomMemberId],
  );

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen={defaultOpen}
      trigger={
        <div className={`${PARENT_CLASS}__heading`}>
          <p className="heading">
            <DocumentsIcon />
            {t('dataRoom')}
          </p>
          <DocumentStatusBadge numberOfCheckedFiles={checkedFilesIds.length} />
        </div>
      }
    >
      {showSelectAllCheckbox && (
        <Checkbox
          className="select-all-checkbox"
          disabled={isLoadingHasFullAccess}
          checked={hasFullAccess}
          label={t('selectAll')}
          onChange={() => onHasFullAccessChange?.(!hasFullAccess)}
        />
      )}
      <FileAccessSelector
        hasLoading
        refetchFlag={refetchFlag}
        checkedFilesIds={checkedFilesIds}
        getBaseNodes={getChildrenForNode}
        getChildrenForNode={getChildrenForNode}
        updateFileAccess={updateFileAccess}
        checkIfNodeIsDisabled={checkIfNodeIsDisabled}
      />
    </CollapsibleSection>
  );
};

export default MemberFileAccessSelector;
